import React, { useState, useEffect, useCallback } from 'react';
import { Package, Bell, XCircle, RefreshCw, Loader2, BellOff, CheckCircle2, Filter } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

interface AdminStockAlertItem {
  userId: string;
  sku: string;
  title: string;
  brand: string;
  category: string;
  qtyWanted: number;
  createdAt: string;
  status: 'active' | 'fulfilled' | 'cancelled';
  fulfilledAt: string | null;
  clientCompany: string;
  an_conto: number | null;
}

const STATUS_LABEL: Record<string, string> = { active: 'Attivo', fulfilled: 'Notificato', cancelled: 'Annullato' };
const STATUS_COLOR: Record<string, string> = {
  active:    'bg-blue-100 text-blue-700',
  fulfilled: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export default function AdminAlertStockPage() {
  const [alerts, setAlerts]     = useState<AdminStockAlertItem[]>([]);
  const [loading, setLoading]   = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [actionId, setActionId] = useState<string | null>(null); // userId::sku in corso

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?filter_status=${filterStatus}` : '';
      const { data } = await api.get<{ items: AdminStockAlertItem[]; total: number }>(
        `/api/admin/stock-alerts${params}`
      );
      setAlerts(data.items);
    } catch {
      toast.error('Errore nel caricamento degli avvisi');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const fulfill = async (item: AdminStockAlertItem) => {
    const id = `${item.userId}::${item.sku}`;
    setActionId(id);
    try {
      await api.post(`/api/admin/stock-alerts/${item.userId}/${item.sku}/fulfill`);
      toast.success(`Cliente notificato per ${item.title}`);
      setAlerts(prev => prev.map(a =>
        a.userId === item.userId && a.sku === item.sku
          ? { ...a, status: 'fulfilled', fulfilledAt: new Date().toISOString() }
          : a
      ));
    } catch {
      toast.error('Errore durante la notifica');
    } finally {
      setActionId(null);
    }
  };

  const remove = async (item: AdminStockAlertItem) => {
    const id = `${item.userId}::${item.sku}`;
    setActionId(id);
    try {
      await api.delete(`/api/admin/stock-alerts/${item.userId}/${item.sku}`);
      toast.success('Avviso eliminato');
      setAlerts(prev => prev.filter(a => !(a.userId === item.userId && a.sku === item.sku)));
    } catch {
      toast.error('Errore durante l\'eliminazione');
    } finally {
      setActionId(null);
    }
  };

  const activeCount = alerts.filter(a => a.status === 'active').length;

  return (
    <div className="p-6" data-testid="admin-alert-stock-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Avvisi Disponibilità</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {loading ? 'Caricamento…' : `${activeCount} ${activeCount === 1 ? 'richiesta attiva' : 'richieste attive'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Filtro status */}
          <div className="flex items-center gap-1.5 border rounded-lg px-3 py-1.5 bg-white text-sm">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="text-sm bg-transparent outline-none"
              data-testid="admin-alert-filter"
            >
              <option value="">Tutti</option>
              <option value="active">Attivi</option>
              <option value="fulfilled">Notificati</option>
            </select>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-1.5 border rounded-lg px-3 py-1.5 bg-white text-sm hover:bg-secondary/50 transition-colors"
            data-testid="admin-alert-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Caricamento…
        </div>
      ) : alerts.length === 0 ? (
        <div className="rounded-xl border bg-white p-12 flex flex-col items-center text-center gap-3" data-testid="admin-alert-empty">
          <BellOff className="w-10 h-10 text-muted-foreground/40" />
          <p className="font-medium">Nessun avviso{filterStatus ? ` con stato "${STATUS_LABEL[filterStatus]}"` : ''}</p>
          <p className="text-sm text-muted-foreground">Gli avvisi vengono creati dai clienti quando un articolo è esaurito.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-alert-table">
            <thead>
              <tr className="border-b bg-[hsl(var(--dtk-canvas))]">
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Cliente</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Prodotto</th>
                <th className="text-center px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Qtà</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase hidden md:table-cell">Data</th>
                <th className="text-center px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Stato</th>
                <th className="text-right px-4 py-3 w-44" />
              </tr>
            </thead>
            <tbody>
              {alerts.map(a => {
                const id = `${a.userId}::${a.sku}`;
                const busy = actionId === id;
                return (
                  <tr key={id} className="border-b last:border-b-0 hover:bg-secondary/30" data-testid={`admin-alert-row-${a.sku}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{a.clientCompany}</div>
                      {a.an_conto && (
                        <div className="text-[10px] text-muted-foreground font-mono">{a.an_conto}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-[hsl(214,58%,41%)]">{a.sku}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[200px]">{a.title}</div>
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">{a.qtyWanted}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                      {new Date(a.createdAt).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${STATUS_COLOR[a.status] ?? ''}`}>
                        {STATUS_LABEL[a.status] ?? a.status}
                      </span>
                      {a.fulfilledAt && (
                        <div className="text-[9px] text-muted-foreground mt-0.5">
                          {new Date(a.fulfilledAt).toLocaleDateString('it-IT')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {a.status === 'active' && (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => fulfill(a)}
                            disabled={busy}
                            className="h-7 px-2.5 rounded-md bg-emerald-500 text-white text-[10px] font-medium flex items-center gap-1 hover:bg-emerald-600 disabled:opacity-50"
                            data-testid={`admin-alert-fulfill-${a.sku}`}
                          >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                            Notifica
                          </button>
                          <button
                            onClick={() => remove(a)}
                            disabled={busy}
                            className="h-7 px-2.5 rounded-md border text-[10px] font-medium flex items-center gap-1 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                            data-testid={`admin-alert-delete-${a.sku}`}
                          >
                            <XCircle className="w-3 h-3" />
                            Elimina
                          </button>
                        </div>
                      )}
                      {a.status === 'fulfilled' && (
                        <div className="flex items-center justify-end gap-1 text-emerald-600 text-[10px]">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Notificato
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
